import { useState } from 'react'

import * as Sentry from '@sentry/browser'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { buildWebpageUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import {
  clearVATNumber,
  getVATAbbreviationByCountry,
} from 'modules/billing/components/PaymentGateway/BillingInfoSection/helpers'
import { ChargebeeSubscriptionForm } from 'modules/billing/components/PaymentGateway/FullScreenStep/form'
import { TAXID_PRO_COUNTRIES } from 'modules/billing/components/PaymentGateway/FullScreenStep/helpers'

interface ValidationMeta {
  name: string
  address: string
  zipCode: string
  state: string
}

interface ValidationResponse {
  isValid: boolean | null
  tinCompact?: string
  tinStandard?: string
  error?: string
  meta?: ValidationMeta
}

const validateVat = async ({
  tin,
  country,
  captureExplicitSentryErrors,
}: {
  tin: string
  country: string
  captureExplicitSentryErrors: boolean
}): Promise<ValidationResponse> => {
  try {
    const fetchUrl = new URL(buildWebpageUrl('/api/tax_id_validation'))
    fetchUrl.searchParams.append('country', country)
    fetchUrl.searchParams.append('tin', tin)

    const response = await fetch(fetchUrl.href)

    return response.json()
  } catch (e) {
    if (captureExplicitSentryErrors) {
      Sentry.captureException(e)
    }
    return { isValid: null }
  }
}

const handleInvalidVatError = (form: ChargebeeSubscriptionForm, country: string) => {
  track(trackingEvents.billing.upgradePlanError, {
    error_message: 'Invalid vat number',
    value: form.get('vat_number').value,
  })
  form.setErrors({
    vat_number: [
      `${getVATAbbreviationByCountry(country)} ${i18n.t(
        'pricing_plans.billing_information.form.validation.is_invalid'
      )}`,
    ],
  })
}

const cleanAndSetVatNumber = (form: ChargebeeSubscriptionForm) => {
  form.setValues({
    vat_number: clearVATNumber(form.get('vat_number').value, form.get('country').value),
  })
}

/*
  Auto fill billing info for Brazil.
  For now this is only possible for users from Brazil, we use NFE.io api to get data from the vat number.
*/
const autoFillBillingInfo = ({
  form,
  isValid,
  country,
  meta,
}: {
  form: ChargebeeSubscriptionForm
  isValid: boolean | null
  country: string
  meta?: ValidationMeta
}) => {
  if (!isValid || country !== 'br' || !meta) {
    return
  }

  form.setValues({
    company: meta.name || '',
    address_line: meta.address || '',
    zip: meta.zipCode || '',
    state: meta.state || '',
  })
}

export const useValidateVatNumber = (
  form: ChargebeeSubscriptionForm,
  captureExplicitSentryErrors: boolean
) => {
  const [isLoading, setIsLoading] = useState(false)
  const [validatedVat, setValidatedVat] = useState('')

  const validate = async () => {
    const vatNumber = form.get('vat_number').value
    const country = form.get('country').value

    // we return if the vatNumber has already been validated previously
    if (!vatNumber || vatNumber === validatedVat) {
      return
    }

    cleanAndSetVatNumber(form)

    if (TAXID_PRO_COUNTRIES.includes(country)) {
      setIsLoading(true)
      try {
        const res = await validateVat({
          tin: vatNumber,
          country,
          captureExplicitSentryErrors,
        })
        const { isValid } = res

        if (validatedVat !== vatNumber) {
          setValidatedVat(vatNumber)
        }
        // if externally validated we save these values
        if (typeof isValid === 'boolean') {
          form.setValues({
            vat_has_been_validated: true,
            vat_validation: isValid,
          })
        }

        autoFillBillingInfo({ form, isValid, country, meta: res.meta })

        if (isValid === false || (isValid === null && !form.isValidTaxId)) {
          setIsLoading(false)
          // FIXME: Check if form country is fine or we need mainLegalEntity?.attributes?.country
          handleInvalidVatError(form, country)
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`❗️ Error ${e}`)
      } finally {
        setIsLoading(false)
      }
    } else if (!form.isValidTaxId) {
      // FIXME: Check if form country is fine or we need mainLegalEntity?.attributes?.country
      handleInvalidVatError(form, country)
    }
  }

  return { isLoading, validate }
}
